import { useEffect, useLayoutEffect, useRef, useSyncExternalStore, type RefObject } from "react";

/**
 * What the person has in view, for the command bar and the keys that work anywhere (BUILD_SPEC §4.3): the issue open in
 * its page or peek, the issues selected in a list or the backlog, and what the page does itself for `c` and `/`. Each
 * page says what it shows as it shows it, and takes it back after.
 */

/** A value kept outside React, which components read as it changes. */
export function store<T>() {
  let value: T | null = null;
  const listeners = new Set<() => void>();
  return {
    set: (next: T | null) => {
      value = next;
      for (const listener of listeners) listener();
    },
    get: () => value,
    subscribe: (onChange: () => void) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
  };
}

/** The latest of a callback, for a function made once to call it. */
function useLatest<T>(value: T): RefObject<T> {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

/** What the command bar asks of the issue in view, which its page does: open a dialog, or a field to type in. */
export type IssueAction = "clone" | "move" | "delete" | "labels" | "estimate" | "due";
export type IssueInView = { id: string; key: string; projectId: string; act: (action: IssueAction) => void };
export type SelectionInView = { projectId: string; issueIds: readonly string[]; clear: () => void };
/** What `c` and `/` do on a page that has its own: a new issue in its project, its own search. */
export type PageKeys = { create?: () => void; search?: () => void };

const issueInView = store<IssueInView>();
const selectionInView = store<SelectionInView>();
const pageKeys = store<PageKeys>();

/** The issue in view, while its page or peek shows it, and what its page does when the command bar asks. */
export function useShowsIssue(
  issue: { id: string; key: string; projectId: string } | null,
  act: (action: IssueAction) => void,
): void {
  const latest = useLatest(act);
  const id = issue?.id ?? null;
  const key = issue?.key ?? null;
  const projectId = issue?.projectId ?? null;
  useEffect(() => {
    if (id === null || key === null || projectId === null) return;
    const shown: IssueInView = { id, key, projectId, act: (action) => latest.current(action) };
    issueInView.set(shown);
    return () => {
      if (issueInView.get() === shown) issueInView.set(null);
    };
  }, [id, key, projectId, latest]);
}

export function useIssueInView(): IssueInView | null {
  return useSyncExternalStore(issueInView.subscribe, issueInView.get, () => null);
}

/** The issues selected in a list or the backlog, while there are any. */
export function useShowsSelection(projectId: string | null, issueIds: readonly string[], clear: () => void): void {
  const latest = useLatest(clear);
  const ids = issueIds.join(",");
  useEffect(() => {
    if (projectId === null || ids === "") return;
    const shown: SelectionInView = { projectId, issueIds: ids.split(","), clear: () => latest.current() };
    selectionInView.set(shown);
    return () => {
      if (selectionInView.get() === shown) selectionInView.set(null);
    };
  }, [projectId, ids, latest]);
}

export function useSelectionInView(): SelectionInView | null {
  return useSyncExternalStore(selectionInView.subscribe, selectionInView.get, () => null);
}

/** What this page does for `c` and `/`, where it has its own; elsewhere they open the command bar. */
export function usePageKeys(keys: PageKeys): void {
  const create = useLatest(keys.create);
  const search = useLatest(keys.search);
  const creates = keys.create !== undefined;
  const searches = keys.search !== undefined;
  useEffect(() => {
    if (!creates && !searches) return;
    const held: PageKeys = {
      ...(creates ? { create: () => create.current?.() } : {}),
      ...(searches ? { search: () => search.current?.() } : {}),
    };
    pageKeys.set(held);
    return () => {
      if (pageKeys.get() === held) pageKeys.set(null);
    };
  }, [creates, searches, create, search]);
}

export function usePageKeysInView(): PageKeys | null {
  return useSyncExternalStore(pageKeys.subscribe, pageKeys.get, () => null);
}
